import React, { useState, useEffect } from "react";

/**
 * TeamPage.js — Separate page for "Meet Our Team"
 * -------------------------------------------------
 * Standalone (not linked yet). Updated per your direction:
 *  - Flip cards on hover (front: photo+name; back: quote ONLY)
 *  - Smaller cards so they don't dominate the screen
 *  - Section headings beautified (subtitle + gradient rule)
 *  - Page uses the SAME color scheme as the main site (subtle gradient wash)
 *  - Transparent LAW SYMBOL (⚖) watermark fixed in the background while scrolling
 *  - Clickable cards (onSelect(member)) ready for future /team/:slug pages
 *  - Clicking **Adv. Pradhyuman Vashist** opens a dedicated detail view page (inside this file) styled like your reference
 */

// ===== Color tokens (aligned with main site) =====
const COLORS = {
  primary: "#2B4C65",
  accent: "#6B8BA4",
  pale: "#95B4CC",
  ink: "#1D1C22",
  white: "#FFFFFF",
};

// ===== Icons (simple SVGs) =====
const IconMail = ({ className = "", style = {} }) => (
  <svg className={className} style={style} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M20 4H4a2 2 0 00-2 2v12a2 2 0 002 2h16a2 2 0 002-2V6a2 2 0 00-2-2Zm0 4-8 5-8-5V6l8 5 8-5v2Z"/>
  </svg>
);
const IconLinkedIn = ({ className = "", style = {} }) => (
  <svg className={className} style={style} viewBox="0 0 24 24" aria-hidden>
    <rect x="2" y="2" width="20" height="20" rx="3" ry="3" fill="currentColor" opacity=".18"/>
    <text x="7.6" y="16" fontSize="10" fontWeight="700" fill="currentColor">in</text>
  </svg>
);

// ===== Utilities =====
const slug = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
// Serve files from public/ reliably even when the app isn't hosted at root
const fromPublic = (p) => `${process.env.PUBLIC_URL}${p}`;

function SectionHeader({ title, subtitle }) {
  return (
    <header className="mb-6">
      {subtitle && (
        <div className="text-[11px] tracking-widest uppercase text-slate-500 mb-1">{subtitle}</div>
      )}
      <h2 className="text-2xl md:text-3xl font-semibold" style={{ color: COLORS.ink }}>{title}</h2>
      <div className="h-px mt-2 bg-gradient-to-r from-[var(--pale)] via-[var(--accent)] to-transparent" style={{"--pale": COLORS.pale, "--accent": COLORS.accent}} />
    </header>
  );
}

function FlipCard({ frontImg, frontName, frontRole, backQuote, onClick }) {
    return (
      <button
        type="button"
        onClick={onClick}
        className="group relative w-full max-w-[260px] sm:max-w-[280px] mx-auto text-left rounded-2xl overflow-hidden border bg-white shadow-sm transition hover:shadow-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
        style={{ borderColor: COLORS.pale }}
        aria-label={`${frontName}${frontRole ? ", " + frontRole : ""}`}
      >
        {/* 3D flip container */}
        <div className="[perspective:1200px]">
          <div className="relative aspect-[4/5] [transform-style:preserve-3d] transition-transform duration-500 group-hover:[transform:rotateY(180deg)]">
            {/* FRONT */}
            <div className="absolute inset-0 [backface-visibility:hidden]">
              {frontImg ? (
                <img
                  src={frontImg}
                  alt={frontName}
                  className="w-full h-full object-cover object-top"
                  loading="lazy"
                />
              ) : (
                <div className="w-full h-full bg-slate-100" />
              )}
              <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-[rgba(0,0,0,0.55)] to-transparent">
                <div className="text-white font-semibold text-sm leading-tight">
                  {frontName || "Name TBD"}
                </div>
                {frontRole && <div className="text-[11px] text-white/80">{frontRole}</div>}
              </div>
            </div>
  
            {/* BACK (quote top + CTA bottom-right) */}
            <div
              className="absolute inset-0 [backface-visibility:hidden] [transform:rotateY(180deg)]"
              style={{
                background: `linear-gradient(135deg, ${COLORS.pale} 0%, ${COLORS.accent} 60%, ${COLORS.primary} 100%)`,
              }}
            >
              {/* Quote at top */}
              <div className="p-4 pr-5">
                <div className="text-white/90 text-3xl leading-none mb-1">“</div>
                <blockquote className="text-white/95 text-sm leading-6">
                  {backQuote
                    ? backQuote
                    : "Profile coming soon—more about this colleague will appear here."}
                </blockquote>
                <div className="text-[11px] text-white/80 mt-2">— {frontName || "Team Member"}</div>
              </div>
  
              {/* Bottom-right CTA (the whole card is clickable; this is visual) */}
              <div className="absolute bottom-3 right-3">
                <span
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/95 text-[12px] font-semibold"
                  style={{ color: COLORS.primary }}
                >
                  Know more
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M5 12h14M13 5l6.5 7L13 19"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </div>
            </div>
          </div>
        </div>
      </button>
    );
  }
  

// ===== Member detail page (for Adv. Pradhyuman Vashist) =====
function MemberDetailPage({ member, onBack }) {
  const {
    name,
    role,
    img,
    location = "Delhi",
    linkedin = "#",
    email = "#",
    practiceAreas = [],
    sectors = [],
    overview = [],
  } = member || {};

  return (
    <main className="relative min-h-screen" style={{ background: COLORS.white }}>
      {/* Background wash + watermark */}
      <div className="fixed inset-0 -z-10" aria-hidden>
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(135deg, ${COLORS.pale} 0%, ${COLORS.accent} 35%, ${COLORS.primary} 100%)`,
            opacity: 0.06,
          }}
        />
        <div className="absolute inset-y-0 right-[-8vw] flex items-center justify-center pointer-events-none select-none">
          <div className="text-[40rem] leading-none" style={{ color: COLORS.primary, opacity: 0.04 }}>
            ⚖
          </div>
        </div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
        {/* Crumb + Back */}
        <div className="flex items-center justify-between mb-6">
          <div className="text-xs text-slate-600">
            Home <span className="opacity-60">›</span> People
          </div>
          <button
            onClick={onBack}
            className="px-3 py-1.5 rounded-lg text-xs border"
            style={{ borderColor: COLORS.pale, color: COLORS.primary }}
          >
            Back
          </button>
        </div>

        {/* Desktop: 2 columns. Left = portrait + quote. Right = identity + practice/sectors + overview. */}
        <div className="grid lg:grid-cols-12 gap-8 items-start">
          {/* LEFT COLUMN */}
          <div className="lg:col-span-5 space-y-8 order-2 lg:order-1">
            {/* Portrait: fixed frame so any image fits without breaking layout */}
            <div className="rounded-3xl overflow-hidden shadow-lg bg-slate-900">
              <div className="relative w-full aspect-[4/5] max-h-[720px]">
                {img ? (
                  <img
                    src={img}
                    alt={name}
                    className="absolute inset-0 w-full h-full object-cover object-top"
                  />
                ) : (
                  <div className="absolute inset-0 bg-slate-800" />
                )}
              </div>
            </div>

            {/* Pull-quote */}
            <div className="relative rounded-3xl border p-6 overflow-hidden" style={{ borderColor: COLORS.pale }}>
              {img && (
                <img
                  src={img}
                  alt="ghost"
                  className="absolute inset-0 w-full h-full object-cover opacity-10 -z-10 object-top"
                />
              )}
              <div className="text-4xl leading-none mb-3" style={{ color: COLORS.accent }}>
                “
              </div>
              <blockquote className="text-[17px] leading-7" style={{ color: COLORS.primary }}>
                Law across borders demands precision and empathy—every brief is a real person’s life moving across systems. Strategy is knowing which lever to pull, and when.
              </blockquote>
              <div className="mt-3 text-sm" style={{ color: COLORS.accent }}>
                — {name}
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN */}
          <div className="lg:col-span-7 space-y-8 order-1 lg:order-2">
            {/* Identity & meta */}
            <div>
              <h1 className="text-4xl md:text-5xl font-semibold" style={{ color: COLORS.primary }}>
                {name}
              </h1>
              <div className="mt-1 text-lg" style={{ color: COLORS.accent }}>
                {role}
              </div>

              {/* Icons + location */}
              <div className="mt-3 flex items-center gap-3">
                <a
                  href={linkedin}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="LinkedIn"
                  className="inline-flex items-center justify-center w-9 h-9 rounded-full"
                  style={{ color: COLORS.primary }}
                >
                  <IconLinkedIn />
                </a>
                <a
                  href={email}
                  aria-label="Email"
                  className="inline-flex items-center justify-center w-9 h-9 rounded-full"
                  style={{ color: COLORS.primary }}
                >
                  <IconMail />
                </a>
                <span
                  className="ml-auto inline-flex items-center gap-1 text-sm"
                  style={{ color: COLORS.primary }}
                >
                  <svg
                    viewBox="0 0 24 24"
                    width="18"
                    height="18"
                    fill="currentColor"
                    aria-hidden
                  >
                    <path d="M12 2a7 7 0 00-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 00-7-7zm0 9.5a2.5 2.5 0 110-5 2.5 2.5 0 010 5z" />
                  </svg>
                  {location}
                </span>
              </div>

              <div className="mt-4 h-px" style={{ background: COLORS.pale }} />
            </div>

            {/* Practice areas & Sectors */}
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <div className="text-sm font-semibold mb-1" style={{ color: COLORS.accent }}>
                  Practice Areas
                </div>
                <ul className="text-slate-800 space-y-1 text-[15px]">
                  {practiceAreas.map((p) => (
                    <li key={p}>{p}</li>
                  ))}
                </ul>
              </div>
              <div>
                <div className="text-sm font-semibold mb-1" style={{ color: COLORS.accent }}>
                  Sectors
                </div>
                <ul className="text-slate-800 space-y-1 text-[15px]">
                  {sectors.map((s) => (
                    <li key={s}>{s}</li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Overview */}
            <div>
              <div className="text-sm font-semibold mb-2" style={{ color: COLORS.accent }}>
                Overview
              </div>
              <div className="prose prose-slate max-w-none text-[15px] leading-7">
                {overview.map((para, i) => (
                  <p key={i} style={{ color: COLORS.ink }}>
                    {para}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}

// ===== Data (replace with real details as you send them) =====
const DATA = {
  directors: [
    {
      name: "Adv. Aakarsh Tomar",
      role: "Founding Partner",
      // img: fromPublic("/people/aakarsh-tomar.jpg"), // add when available
      quote: "Strategy turns rights into results; the craft is in sequencing facts, law, and timing with absolute clarity.",
    },
    {
      name: "Adv. Pradhyuman Vashisht",
      role: "Founding Partner",
      img: "/people/pradhyuman.jpg",
      quote: "Clarity beats speed; across jurisdictions, the brief that persuades is the one the court can trust at a single careful read.",
      location: "Delhi",
      linkedin: "#",
      email: "#",
      practiceAreas: [
        "Immigration & Nationality Law",
        "Asylum & Human Rights",
        "Cross‑Border Litigation",
        "Deportation & Removal Defence",
        "Visa Strategy & Compliance Advisory",
      ],
      sectors: [
        "Individuals & Families",
        "Startups & Founders",
        "International Clients",
      ],
      overview: [
        "Pradhyuman Vashisht is a globally experienced legal professional and a founding Director of Rex Counsel LLP. He is recognised for a dynamic litigation practice and an international perspective that bridges courts and clients across jurisdictions.",
        "He holds an LL.M. in Criminal Law and Human Rights from the University of Sussex, United Kingdom. His academic work explored the role of AI in predictive policing—reflecting a progressive outlook on technology, rights, and due process.",
        "Pradhyuman regularly handles matters in India, the United Kingdom, and the United States—particularly immigration, asylum, human rights, and cross‑border litigation. He advises on complex visa questions, deportation defence and refugee protection, bringing a nuanced understanding of global frameworks and policy.",
        "With practice rooted in the Supreme Court and High Courts, he also appears before tribunals and forums across jurisdictions. His international experience includes work with Singhania & Co. LLP (UK), contributing to arbitration and commercial mandates involving British and foreign clients.",
        "He combines deep legal acumen with a global vision, offering tailored solutions marked by integrity, strategic thinking, and client‑focused advocacy.",
      ],
    },
    {
      name: "Adv. Digvijay Bhuyan",
      role: "Founding Partner",
      img: "/people/Dj.jpg",
      quote: "Precise drafting is strategy on paper—the right word can save months in court.",
      location: "Delhi",
      linkedin: "#",
      email: "#",
      practiceAreas: [
        "Legal Drafting & Research",
        "Contracts, Agreements & Notices",
        "Petitions & Written Submissions",
        "Litigation Strategy",
        "International & Comparative Law",
      ],
      sectors: [
        "Corporate & Startups",
        "Individuals & Families",
        "Academia & Policy",
      ],
      overview: [
        "Digvijay Bhuyan is one of the founding directors of Rex Counsel LLP with a strong specialization in legal drafting and research. He prepares contracts, petitions, notices and agreements known for clarity, precision and strategic value.",
        "He has authored and published articles on contemporary legal topics, contributing to both academic and professional discourse.",
        "He holds a Bachelor of Arts in Global Affairs and an LL.B. from O.P. Jindal Global University, and an LL.M. in Criminal Law & Criminal Justice from the University of Sussex, United Kingdom—deepening his understanding of criminal justice and international standards.",
        "His academic background and practical insight make him a reliable advisor in complex legal matters, both in India and internationally.",
      ],
    },
  ],

  ImmigrationExpert: [
    {
      name: "Shivam Tiwari",
      role: "Foreign Law Expert",
      img: "/people/shivamtiwar.jpeg",
      quote: "Foreign law and immigration are not just about crossing borders—they are about securing rights, opportunities, and a lawful future in a globalized world",
      linkedin: "#",
      email: "#",
      practiceAreas: [
        "Immigration & Global Mobility",
        "Assylum",
        "Cross‑Border Compliance",
        "Advisory for NRIs & Foreign Entities",
        "Civil & Criminal Litigation",
        "Appellate & Tribunal Practice (HC, NCLT, DRT, DRAT)",
      ],
      sectors: [
        "International Clients",
        "Foreign Commercial Entities",
        "Startups & SMEs",
      ],
      overview: [
        "Shivam Tiwari is a legal professional with a strong foundation in litigation, legal research, and international legal processes. He has worked as a Legal Associate at the Allahabad High Court, where he handled a wide range of civil, criminal, and writ matters, gaining deep experience in courtroom practice, procedural law, and client advocacy.",
        "Shivam has drafted a variety of legal documents, including writ petitions, legal notices, bail applications, and affidavits, which are known for their clarity, precision, and strategic structure.",
        "He further expanded his legal expertise by working with TPL Global, LLC, where he specialized in U.S. immigration law, handling matters related to asylum, EADs, and ICE compliance. His responsibilities included legal drafting, motion writing, and direct coordination with clients and government agencies, equipping him with a cross-border legal perspective",
        "In addition, Shivam has practical knowledge in DGFT procedures, RTD documentation, and regulatory compliance, making him well-suited for both individual legal representation and corporate legal support. Known for his ethical approach and attention to detail, Shivam Tiwari continues to establish himself as a dependable and forward-thinking legal professional.",
      ],
    },
  ],

  seniorAssociates: [
    {
      name: "Akash Sharma",
      role: "Senior Associate",
      img: "/people/akash.jpeg",
      quote: "International practice is detail and discipline—every cross‑border matter turns on documents, timing, and the right forum.",
      linkedin: "#",
      email: "#",
      practiceAreas: [
        "Immigration & Global Mobility",
        "Cross‑Border Compliance",
        "Advisory for NRIs & Foreign Entities",
        "Civil & Criminal Litigation",
        "Appellate & Tribunal Practice (HC, NCLT, DRT, DRAT)",
      ],
      sectors: [
        "International Clients",
        "Foreign Commercial Entities",
        "Startups & SMEs",
      ],
      overview: [
        "Akash Sharma focuses on the firm’s international practice—immigration law, cross‑border compliance, and advisory services for non‑resident individuals and foreign commercial entities.",
        "He regularly navigates multi‑jurisdictional regulations, international documentation, and legal frameworks that shape global mobility and cross‑border business operations.",
        "Alongside his international portfolio, Akash maintains an active domestic practice across civil and criminal jurisdictions, appearing before the High Court and tribunals including the NCLT, DRT, and DRAT.",
        "With balanced experience across local procedures and global systems, he brings strategic, reliable advocacy to complex matters.",
      ],
    },
    {
      name: "Salil Somvanshi",
      role: "Senior Associate",
      img: "/people/salil.jpg",
      quote: "Great operations turn intent into delivery—systems protect brands while people focus on the work that matters.",
      location: "Allahabad",
      linkedin: "#",
      email: "#",
      practiceAreas: [
        "Operations & Governance",
        "Digital Brand & IP Protection",
        "Online Reputation Management",
        "Platform Disputes & Takedowns",
        "Process & Resource Planning",
      ],
      sectors: [
        "Technology",
        "Creators & Public Figures",
        "Startups & SMEs",
        "Consumer Brands",
      ],
      overview: [
        "Salil Somvanshi serves as Chief Operating Officer at Rex Counsel LLP, leading cross‑functional operations at the intersection of law, technology and strategic brand consulting.",
        "He drives governance, resourcing and process improvements to deliver thoughtful, research‑backed solutions across intellectual property, copyright, digital brand protection and online reputation matters.",
        "Salil is currently a Research Associate at the High Court of Judicature at Allahabad and works closely with teams to tackle digital challenges—from impersonation and content takedowns to navigating platform norms and improving strategic visibility.",
        "Under his leadership, Rex Counsel manages global digital assets and reputations for brands, entrepreneurs and public figures, providing tailored solutions that protect and amplify presence across channels.",
      ],
    },
  ],
  associates: [
    {
      name: "Brahma Kamal Srivastava",
      role: "Associate",
      img: "/people/brahmkamal.jpeg",
      quote: "Dynamic, driven, digitally aware—cyber disputes demand classical rigor with modern fluency in evidence and platforms.",
      linkedin: "#",
      email: "#",
      practiceAreas: [
        "Civil & Criminal Litigation",
        "Constitutional Law",
        "Cyber Law & Cyber Crime",
        "Digital Evidence & Defamation",
        "Privacy & Data Issues",
      ],
      sectors: [
        "Individuals & Families",
        "Technology & Platforms",
        "Cross‑Border Disputes",
      ],
      overview: [
        "Brahma Kamal Srivastava is a rising litigator at Rex Counsel LLP known for precision in court and a sharp, modern legal approach.",
        "Practicing at the High Court, he handles civil, criminal, constitutional, and cyber law matters—delivering strategic, results‑driven solutions.",
        "With growing command over cyber law and cyber‑crime litigation, he advises on data breaches, online fraud, digital evidence, defamation and privacy violations, pairing legal clarity with technical understanding.",
        "His international exposure and research across comparative, human rights, and technology‑driven law amplify his capability on cross‑border disputes and modern trends. He has contributed to major litigation efforts and strategy with reputed firms and legal teams.",
        "Rooted in legal aid and access‑to‑justice work, he brings passion, precision and progressive thinking to every matter—blending courtroom intensity with contemporary insight.",
      ],
    },
    {
      name: "Raj",
      role: "Associate — Analyst",
      img: "/people/raj.jpg", 
      quote: "Legal strategy meets digital reality—protect the brand, defend the rights, and make platforms work for clients.",
      linkedin: "#",
      email: "#",
      practiceAreas: [
        "Copyright & Intellectual Property",
        "Digital Brand Strategy",
        "Online Reputation Management",
        "Platform & Account Issues (FB/IG)",
        "Digital Asset Protection",
        "Technical/Legal Research",
      ],
      sectors: [
        "International Clients",
        "Creators & Public Figures",
        "Consumer Brands",
        "Technology & Platforms",
      ],
      overview: [
        "Raj is a multi‑skilled analyst‑associate at Rex Counsel LLP, operating at the intersection of law, technology and digital brand strategy.",
        "With a strong legal background and growing IP/copyright expertise, he supports research‑driven solutions for a fast‑moving digital landscape.",
        "He manages global digital portfolios for an international clientele, assisting with social media strategy, online reputation management and digital asset protection.",
        "His work includes resolving complex platform issues—account restrictions, identity concerns and visibility challenges on Facebook, Instagram and other networks.",
        "With a sharp eye for strategy and detail, Raj plays a pivotal role in technical research, IP matters and digital outreach—translating legal knowledge into practical, platform‑ready solutions.",
      ],
    },
    {
      name: "Suhil Bose",
      role: "Associate",
      img: "/people/suhilbose.jpeg", 
      quote: "Preparation and steady advocacy move courts—clarity in pleadings, precision in reliefs, persistence in results.",
      linkedin: "#",
      email: "#",
      practiceAreas: [
        "Civil Litigation",
        "Criminal Defence & Bail",
        "Writ Jurisdiction",
        "Pleadings under CPC & Special Statutes",
        "Appellate Practice",
      ],
      sectors: [
        "Individuals & Families",
        "Startups & SMEs",
        "Public & Regulatory",
      ],
      overview: [
        "Suhil Bose is a distinguished litigator primarily practising before the courts of Delhi, with appearances in the Punjab & Haryana, Allahabad and Calcutta High Courts.",
        "He has handled a broad spectrum of civil and criminal matters including writ petitions, bail applications and petitions under the Civil Procedure Code and other key statutes.",
        "Known for meticulous preparation and unwavering advocacy, Suhil secures robust outcomes through strategic representation and a clear focus on client interests.",
      ],
    },
  ],
};

function Section({ title, subtitle, children }) {
  return (
    <section className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <SectionHeader title={title} subtitle={subtitle} />
      {children}
    </section>
  );
}

/**
 * Props
 * @param {Object} [teamOverrides] - optional override per section if you want to inject real data immediately
 * @param {(member:Object) => void} [onSelect] - click handler to open person page later
 * @param {() => void} [onBack] - optional back button
 */
export default function TeamPage({ teamOverrides, onSelect, onBack }) {
  const team = {
    directors: teamOverrides?.directors || DATA.directors,
    ImmigrationExpert: teamOverrides?.ImmigrationExpert || DATA.ImmigrationExpert,
    seniorAssociates: teamOverrides?.seniorAssociates || DATA.seniorAssociates,
    associates: teamOverrides?.associates || DATA.associates,
  };

  const [view, setView] = useState('grid');
  const [selected, setSelected] = useState(null);

  // ==== Dev checks (lightweight tests) ====
  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return;
    try {
      console.groupCollapsed('TeamPage — dev checks');
      console.assert(team.directors.length === 3, 'Expected 3 director cards');
      console.assert(team.ImmigrationExpert.length === 3, 'Expected 3 Immigration cards');
      console.assert(team.seniorAssociates.length === 3, 'Expected 3 senior associate cards (1 + 2 placeholders)');
      console.assert(team.associates.length === 5, 'Expected 5 associate cards (3 people + 2 placeholders)');
      console.assert(team.directors.some((m) => slug(m.name) === 'adv-pradhyuman-vashisht'), 'Missing Adv. Pradhyuman Vashisht in directors');
      console.assert(team.associates.some((m) => slug(m.name) === 'brahma-kamal-srivastava'), 'Missing Brahma Kamal Srivastava in associates');
      console.assert(team.associates.some((m) => slug(m.name) === 'raj'), 'Missing Raj in associates');
      console.assert(team.associates.some((m) => slug(m.name) === 'suhil-bose'), 'Missing Suhil Bose in associates');
      console.groupEnd();
    } catch (e) {
      // no-op in case console is blocked
    }
  }, [team]);

  if (view === 'detail' && selected) {
    return (
      <MemberDetailPage
        member={selected}
        onBack={() => setView('grid')}
      />
    );
  }
  return (
    <main className="relative min-h-screen" style={{ background: COLORS.white }}>
      {/* Subtle page gradient + fixed watermark behind content */}
      <div className="fixed inset-0 -z-10" aria-hidden>
        <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${COLORS.pale} 0%, ${COLORS.accent} 35%, ${COLORS.primary} 100%)`, opacity: 0.06 }} />
        <div className="absolute inset-y-0 right-[-8vw] flex items-center justify-center pointer-events-none select-none">
          <div className="text-[40rem] leading-none" style={{ color: COLORS.primary, opacity: 0.04 }}>⚖</div>
        </div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-3xl md:text-4xl font-semibold" style={{ color: COLORS.ink }}>Meet the Minds Behind Rex Counsel</h1>
        </div>
        <p className="text-slate-700 max-w-3xl mb-8">At Rex Counsel, our team is more than a group of lawyers — we are strategic partners, advocates, and problem-solvers.
From litigation to cross-border advisory, each member brings deep legal expertise and unwavering integrity to the table.
Together, we combine precision, professionalism, and purpose to deliver clarity and results for every client we represent.</p>
      </div>

      {/* Directors */}
      <Section title="Directors" subtitle="Leadership & Strategy">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
          {team.directors.map((m) => (
            <FlipCard
              key={slug(m.name)}
              frontImg={m.img}
              frontName={m.name}
              frontRole={m.role}
              backQuote={m.quote}
              onClick={() => { if (!m.placeholder) { setSelected(m); setView('detail'); } }}
            />
          ))}
        </div>
      </Section>

      {/* Immigration Experts */}
      <Section title="Immigration Experts" subtitle="Experience in Immigration and Foreign Law">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
          {team.ImmigrationExpert.map((m) => (
            <FlipCard
              key={slug(m.name)}
              frontImg={m.img}
              frontName={m.name}
              frontRole={m.role}
              backQuote={m.quote}
              onClick={() => { if (!m.placeholder) { setSelected(m); setView('detail'); } }}
            />
          ))}
        </div>
      </Section>

      {/* Senior Associates */}
      <Section title="Senior Associates" subtitle="Experience in Action">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
          {team.seniorAssociates.map((m, i) => (
            <FlipCard
              key={slug(m.name) + i}
              frontImg={m.img}
              frontName={m.name}
              frontRole={m.role}
              backQuote={m.quote}
              onClick={() => { if (!m.placeholder) { setSelected(m); setView('detail'); } }}
            />
          ))}
        </div>
      </Section>

      {/* Associates */}
      <Section title="Associates" subtitle="Execution & Research">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-6 pb-14">
          {team.associates.map((m, i) => (
            <FlipCard
              key={slug(m.name) + i}
              frontImg={m.img}
              frontName={m.name}
              frontRole={m.role}
              backQuote={m.quote}
              onClick={() => { if (!m.placeholder) { setSelected(m); setView('detail'); } }}
            />
          ))}
        </div>
      </Section>
    </main>
  );
}
